/**
 * Every tenant on the deployment, on one screen.
 *
 * The question it answers is "which customer needs me today", which until now
 * required opening each organization in turn — so in practice nobody asked it
 * until something had been broken for a while.
 *
 * READ-ONLY, deliberately. Every action is one organization-switch away, on a
 * screen that already has its own capability check and audit trail. A second
 * path to every mutation, reachable by a credential no tenant can see, would be
 * a far larger security surface than this convenience is worth.
 *
 * Not a capability check. `is_platform_operator` is a property of the account
 * rather than of a membership, so `can()` cannot express it — see the type.
 */

import { Link } from "react-router-dom";

import { api } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { useSession } from "../session";

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  plan: string;
  created_at: string;
  deleted_at?: string;
  purge_after?: string;
  sending_paused: boolean;
  sending_ready: boolean;
  messages_sent: number;
  contacts_peak: number;
  recent_sent: number;
  recent_bounced: number;
  recent_complained: number;
  last_send_at?: string;
}

export function Operator() {
  const { session } = useSession();
  const { data, error, loading } = useAsync<{ tenants: TenantRow[] }>(
    (signal) => api.get<{ tenants: TenantRow[] }>("/operator/tenants", { signal }),
    [],
  );

  // The route is reachable by typing it, so it says what it is rather than
  // rendering an empty table. The server answers 404 regardless — this is only
  // about what the person sees.
  if (!session?.user.is_platform_operator) {
    return (
      <div className="oc-empty">
        <div className="oc-empty__title">Not available</div>
        <div className="oc-empty__hint">
          This screen shows every organization on the deployment, and is limited to accounts with
          operator access. It is granted from the command line on the server, never through the
          product.
        </div>
      </div>
    );
  }

  const tenants = data?.tenants ?? [];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Tenants</h1>
        <span className="oc-body-sm oc-text-secondary">
          {tenants.length} {tenants.length === 1 ? "organization" : "organizations"} on this
          deployment
        </span>
      </div>

      <div className="oc-panel" style={{ padding: "var(--oc-gutter)" }}>
        {error && <p className="field-error">{error.message}</p>}
        {loading && !data && <SkeletonRows rows={5} />}

        {!loading && tenants.length === 0 && (
          <div className="oc-empty">
            <div className="oc-empty__title">No organizations yet</div>
            <div className="oc-empty__hint">
              The first one appears here as soon as somebody creates it.
            </div>
          </div>
        )}

        {tenants.length > 0 && (
          // Seven columns, in app.css. Name absorbs the slack and truncates
          // last, matching the message log — it is what people scan.
          <div className="oc-table operator-table">
            <div className="oc-table__head">
              <div className="oc-table__cell">Organization</div>
              <div className="oc-table__cell">Sending</div>
              <div className="oc-table__cell oc-table__cell--right">Sent</div>
              <div className="oc-table__cell oc-table__cell--right">Contacts</div>
              <div className="oc-table__cell oc-table__cell--right">7 days</div>
              <div className="oc-table__cell">Last send</div>
              <div className="oc-table__cell">Created</div>
            </div>
            <div className="oc-table__body">
              {tenants.map((t) => (
                <TenantLine key={t.id} tenant={t} />
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="oc-body-sm oc-text-secondary" style={{ marginTop: "var(--oc-space-3)" }}>
        Sent and contacts are this calendar month, from the usage rollup. The seven-day figures are
        counted from message status and are an indicator rather than a report —{" "}
        <Link to="/analytics">Analytics</Link> inside an organization is the number of record. Every
        request to this screen is recorded.
      </p>
    </>
  );
}

function TenantLine({ tenant: t }: { tenant: TenantRow }) {
  return (
    <div className="oc-table__row">
      <div className="oc-table__cell">
        <span style={{ fontWeight: 500 }}>{t.name}</span>{" "}
        <span className="oc-mono-sm oc-text-muted">{t.slug}</span>
        {t.deleted_at && (
          <span className="oc-status" data-status="failed" style={{ marginLeft: 8 }}>
            Deleting
          </span>
        )}
      </div>

      <div className="oc-table__cell">
        <SendingCell tenant={t} />
      </div>

      <div className="oc-table__cell oc-table__cell--right">{t.messages_sent.toLocaleString()}</div>
      <div className="oc-table__cell oc-table__cell--right">{t.contacts_peak.toLocaleString()}</div>

      <div className="oc-table__cell oc-table__cell--right">
        <Recent tenant={t} />
      </div>

      <div className="oc-table__cell oc-body-sm oc-text-secondary">
        {t.last_send_at ? new Date(t.last_send_at).toLocaleDateString() : "—"}
      </div>
      <div className="oc-table__cell oc-body-sm oc-text-secondary">
        {new Date(t.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}

/**
 * Paused and never-set-up are different states that both mean no mail is going
 * out, and an operator acts differently on each: one is a reputation problem to
 * investigate, the other is onboarding somebody forgot to finish.
 */
function SendingCell({ tenant: t }: { tenant: TenantRow }) {
  if (t.sending_paused) {
    return <span className="oc-status" data-status="bounced">Paused</span>;
  }
  if (!t.sending_ready) {
    return <span className="oc-status" data-status="queued">Not set up</span>;
  }
  return <span className="oc-status" data-status="delivered">Ready</span>;
}

/**
 * Seven days of sends, with failures called out only when there are any.
 *
 * Below the volume floor the rate is shown and never coloured, the same rule
 * Analytics and the Prometheus alerts follow: one bounce in three is not a 33%
 * bounce rate, and a screen that cries wolf on a test send is one nobody reads
 * on the day it matters.
 */
const VOLUME_FLOOR = 50;

function Recent({ tenant: t }: { tenant: TenantRow }) {
  if (t.recent_sent === 0) {
    return <span className="oc-text-muted">—</span>;
  }

  const bad = t.recent_bounced + t.recent_complained;
  if (bad === 0) {
    return <span>{t.recent_sent.toLocaleString()}</span>;
  }

  const rate = bad / t.recent_sent;
  const loud = t.recent_sent >= VOLUME_FLOOR && rate >= 0.04;

  return (
    <span>
      {t.recent_sent.toLocaleString()}{" "}
      <span
        className="oc-mono-sm"
        style={{ color: loud ? "var(--oc-danger)" : "var(--oc-text-secondary)" }}
        title={`${t.recent_bounced} bounced, ${t.recent_complained} complained`}
      >
        {bad} bad
      </span>
    </span>
  );
}
